/**
 * Refund Modal Component
 * 
 * Modal for refunding order items
 * Handles amount calculation and penalty options
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Slider,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
  InputAdornment
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BaseActionModal, { IActionModalProps, IModalChildProps } from './BaseActionModal';
import { ArbitrationAction, IFinancialInfo } from '../../../services/arbitrationService';

interface RefundModalProps extends Omit<IActionModalProps, 'action'> {
  financial?: IFinancialInfo;
  isOrderItem?: boolean;
}

const RefundModalContent: React.FC<IModalChildProps & { 
  financial?: IFinancialInfo;
  isOrderItem?: boolean;
}> = ({
  payload,
  setPayload,
  setIsValid,
  financial,
  isOrderItem = true
}) => {
  const maxAmount = financial?.refundableAmount || 0;
  const [amount, setAmount] = useState(maxAmount);
  const [reason, setReason] = useState('');
  const [applyPenalty, setApplyPenalty] = useState(true);
  const [penaltyPoints, setPenaltyPoints] = useState(10);

  useEffect(() => {
    setPayload({
      amount,
      reason,
      applyPenalty,
      penaltyPoints: applyPenalty ? penaltyPoints : 0
    });
    setIsValid(amount > 0 && reason.length >= 5);
  }, [amount, reason, applyPenalty, penaltyPoints, setPayload, setIsValid]);

  const percentage = maxAmount > 0 ? Math.round((amount / maxAmount) * 100) : 0;

  return (
    <Box>
      <Alert severity="info" icon={<MonetizationOnIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          مبلغ بازپرداخت به کیف پول کاربر واریز می‌شود و به فروشنده جریمه تعلق می‌گیرد.
        </Typography>
      </Alert>

      {/* Financial Summary */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'grey.100', 
        borderRadius: 2,
        mb: 3
      }}>
        <Typography variant="subtitle2" gutterBottom>خلاصه مالی</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">قیمت محصول:</Typography>
          <Typography variant="body2">{(financial?.amount || 0).toLocaleString()} تومان</Typography>
        </Box>
        {(financial?.shippingCost || 0) > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">هزینه ارسال:</Typography>
            <Typography variant="body2">{(financial?.shippingCost || 0).toLocaleString()} تومان</Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" fontWeight="bold">حداکثر قابل بازپرداخت:</Typography>
          <Typography variant="body2" fontWeight="bold" color="primary.main">
            {maxAmount.toLocaleString()} تومان
          </Typography>
        </Box>
      </Box>

      {/* Amount Slider */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">مبلغ بازپرداخت</Typography>
          <Chip 
            label={`${percentage}%`} 
            color={percentage === 100 ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Slider
          value={amount}
          onChange={(_, value) => setAmount(value as number)}
          max={maxAmount}
          step={1000}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v.toLocaleString()} تومان`}
        />
        <TextField
          fullWidth
          type="number"
          value={amount}
          onChange={(e) => setAmount(Math.min(Number(e.target.value), maxAmount))}
          InputProps={{
            endAdornment: <InputAdornment position="end">تومان</InputAdornment>
          }}
          size="small"
        />
      </Box>

      {/* Reason */}
      <TextField
        fullWidth
        multiline
        rows={2}
        label="دلیل بازپرداخت"
        placeholder="مثال: عدم تطابق محصول با توضیحات"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        sx={{ mb: 3 }}
        helperText="این متن به کاربر نمایش داده می‌شود"
      />

      {/* Penalty Options */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'warning.50', 
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'warning.200'
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={applyPenalty}
              onChange={(e) => setApplyPenalty(e.target.checked)}
              color="warning"
            />
          }
          label="اعمال جریمه به فروشنده"
        />
        
        {applyPenalty && (
          <Box sx={{ mt: 2, ml: 4 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              امتیاز منفی:
            </Typography>
            <Slider
              value={penaltyPoints}
              onChange={(_, value) => setPenaltyPoints(value as number)}
              min={1}
              max={50}
              step={1}
              marks={[
                { value: 1, label: '۱' },
                { value: 25, label: '۲۵' },
                { value: 50, label: '۵۰' }
              ]}
              valueLabelDisplay="on"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const RefundModal: React.FC<RefundModalProps> = ({ financial, isOrderItem, ...props }) => {
  const action = isOrderItem ? ArbitrationAction.REFUND_ITEM : ArbitrationAction.REFUND_ORDER;
  
  return (
    <BaseActionModal {...props} action={action}>
      <RefundModalContent 
        financial={financial} 
        isOrderItem={isOrderItem}
        payload={{}} 
        setPayload={() => {}} 
        isValid={false} 
        setIsValid={() => {}} 
      />
    </BaseActionModal>
  );
};

export default RefundModal;

